import type { PropsWithChildren } from "react";
import { createContext, useCallback, useEffect, useState } from "react";
import { withErrorHandling } from "@entrilia/shared/api/axiosHelper";
import type { ApiError } from "@entrilia/shared/api/types";
import { useNotificationContext } from "@entrilia/shared/contexts/NotificationContext";
import useDefinedContext from "@entrilia/shared/hooks/useDefinedContext";
import useFetch from "@entrilia/shared/hooks/useFetch";
import UserAddDialogIcon from "@entrilia/shared/icons/UserAddDialogIcon";
import { logError } from "@entrilia/shared/logging";
import { stringComparerBy } from "@entrilia/shared/utilities/arrayHelper";
import adminApi from "../../../../../api/adminApi";
import type {
  AccessLevelEntity,
  CompanyChange,
  EntitiesAccessConfig,
  OrganizationMember,
  OrganizationRole,
} from "../../../../../api/types/userManagementTypes";
import { allOrganizationRoles } from "../../../../../api/types/userManagementTypes";
import useClientContext from "../../../../../context/useClientContext";
import EmailSuccessDialogIcon from "@entrilia/shared/icons/EmailSuccessDialogIcon";
import { isExternalUserEmail } from "../../../../../utilities/userHelper";
import type { InfoDialogData } from "../../../../common/InfoDialog";
import InfoDialog from "../../../../common/InfoDialog";
import MembersContext from "../MembersContext";
import type { CompanyAccessLevel, CompanyProductWithRoles, CompanyWithAccess, CompanyWithRoles } from "../definitions";
import {
  alignCompaniesAccessToAvailableProducts,
  filterOrganizationRoles,
  getAvailableProductsWithRolesForCompany,
  getEntityLevelAccessClientCodes,
} from "../definitions";

const requireAtLeastOneInEntityLevelAccess = false;

interface ContextValue {
  organizationRole: OrganizationRole | undefined;
  updateOrganizationRole: (role: OrganizationRole) => void;
  emails: string[];
  updateEmails: (emails: string[]) => void;
  selectedCompanies: CompanyWithAccess[];
  updateSelectedCompanies: (companies: CompanyWithAccess[]) => void;
  onCompanyDelete: (clientCode: string) => void;
  selectedCompanyAccess: [CompanyWithAccess, CompanyAccessLevel] | undefined;
  updateSelectedCompanyAccess: (company: CompanyWithAccess, accessLevel: CompanyAccessLevel) => void;
  isValidConfiguration: boolean;
  distributePermissions: () => void;
  sendInvites: () => void;
  handleClose: () => void;
  getDialogTitle: () => string;
  managedMember: OrganizationMember | undefined;
  applyMemberChanges: () => void;
  allowedOrganizationRoles: readonly OrganizationRole[];
  disabledOrganizationRoles: Partial<Record<OrganizationRole, string>>;
  isCurrentUser: (idOrEmail: string) => boolean;
  getAvailableProductsWithRoles: (clientCode: string) => CompanyProductWithRoles[];
  getCompanyEntities: (clientCode: string) => AccessLevelEntity[] | undefined;
  entitiesFetchError: ApiError | undefined;
}

const ownerRoleDisabledForExternalUsersReason =
  "The Owner role can't be assigned to external users. Remove any external email addresses to enable it.";

const sendInvitations = withErrorHandling(adminApi.sendInvitations);
const updateOrganizationMemberAccess = withErrorHandling(adminApi.updateOrganizationMemberAccess);

const InviteMembersContext = createContext<ContextValue | undefined>(undefined);

export const InviteMembersContextProvider = ({ children }: PropsWithChildren) => {
  const { organizationDomains } = useClientContext();
  const { sendNotificationError } = useNotificationContext();

  const [emails, setEmails] = useState<string[]>([]);
  const [organizationRole, setOrganizationRole] = useState<OrganizationRole>();
  const [selectedCompanies, setSelectedCompanies] = useState<CompanyWithAccess[]>([]);
  const [selectedCompanyAccess, setSelectedCompanyAccess] = useState<
    [CompanyWithAccess, CompanyAccessLevel] | undefined
  >();
  const [isValidConfiguration, setIsValidConfiguration] = useState(false);
  const [managedMember, setManagedMember] = useState<OrganizationMember | undefined>();
  const [currentUserUpdated, setCurrentUserUpdated] = useState(false);
  const [infoDialogData, setInfoDialogData] = useState<InfoDialogData>();

  const {
    clientTitle,
    setIsSending,
    inviteMembersDialogData,
    setInviteMembersDialogData,
    setSelectedMembersIds,
    updatePendingInvites,
    updateMembers,
    companies,
    members,
    hasAccessToManageOrganizationOwners,
    isCurrentUser,
    isLoading,
  } = useDefinedContext(MembersContext);

  const isValid = useCallback(() => emails.length > 0 && !!organizationRole, [emails.length, organizationRole]);

  // Business Central answers slowly for an idle company, so entities are loaded here for the companies in play, not with the page
  const entityLevelAccessClientCodesKey = getEntityLevelAccessClientCodes(
    selectedCompanies,
    inviteMembersDialogData?.member,
    companies
  ).join(",");

  const fetchCompaniesEntities = useCallback(async () => {
    try {
      return await adminApi.getOrganizationCompaniesEntities(entityLevelAccessClientCodesKey.split(","));
    } catch (error) {
      logError(error, "[InviteMembersContext] getOrganizationCompaniesEntities");
      throw error;
    }
  }, [entityLevelAccessClientCodesKey]);

  const [companiesEntitiesResp, entitiesFetchError] = useFetch(fetchCompaniesEntities, {
    disabled: entityLevelAccessClientCodesKey === "",
  });

  const getCompanyEntities = useCallback(
    (clientCode: string) =>
      companiesEntitiesResp?.companies.find((company) => company.clientCode === clientCode)?.entities,
    [companiesEntitiesResp]
  );

  const getCompaniesWithSanitizedRoles = useCallback(
    (companiesWithAccess: CompanyWithAccess[], orgRole: OrganizationRole): CompanyWithAccess[] =>
      alignCompaniesAccessToAvailableProducts(orgRole, companiesWithAccess, companies),
    [companies]
  );

  useEffect(() => {
    if (!isValid()) {
      // eslint-disable-next-line @eslint-react/set-state-in-effect
      setIsValidConfiguration(false);
      return;
    }

    if (inviteMembersDialogData?.action === "invite") {
      const memberExists = members.map((member) => member.email).some((email) => emails.includes(email));
      if (memberExists) {
        // eslint-disable-next-line @eslint-react/set-state-in-effect
        setIsValidConfiguration(false);
        return;
      }
    }

    for (const company of selectedCompanies) {
      if (company.roles.length === 0) {
        // eslint-disable-next-line @eslint-react/set-state-in-effect
        setIsValidConfiguration(false);
        return;
      }

      if (requireAtLeastOneInEntityLevelAccess && company.entityIds.length === 0) {
        const companyInfo = companies.find((c) => c.clientCode === company.clientCode);
        if (companyInfo?.entityLevelAccess) {
          // eslint-disable-next-line @eslint-react/set-state-in-effect
          setIsValidConfiguration(false);
          return;
        }
      }
    }
    // eslint-disable-next-line @eslint-react/set-state-in-effect
    setIsValidConfiguration(true);
  }, [companies, emails, organizationRole, selectedCompanies, isValid, inviteMembersDialogData?.action, members]);

  const updateSelectedCompanies = (companiesWithAccess: CompanyWithAccess[], orgRole = organizationRole) => {
    const companiesToUpdate = orgRole
      ? getCompaniesWithSanitizedRoles(companiesWithAccess, orgRole)
      : companiesWithAccess;
    const sortedCompanies = [...companiesToUpdate].sort(stringComparerBy((c) => c.clientCode));
    // eslint-disable-next-line @eslint-react/set-state-in-effect
    setSelectedCompanies(sortedCompanies);
  };

  const handleClose = () => {
    const isCurrentUserUpdated = currentUserUpdated;
    clearEverything();
    setInviteMembersDialogData(undefined);
    if (isCurrentUserUpdated) {
      window.location.reload();
    }
  };

  const handleConfirmationClose = () => {
    setInfoDialogData(undefined);

    if (inviteMembersDialogData?.action === "invite") {
      handleClose();
    }
  };

  const getSelectedEntitiesAccessPerCompany = () => {
    return selectedCompanies
      .filter((c) => {
        const company = companies.find((company) => company.clientCode === c.clientCode);
        return company?.entityLevelAccess;
      })
      .map((c) => ({
        [c.clientCode]: {
          allowedEntityIds: c.entityIds,
          allowAccessToAllEntities: c.accessToAllEntities,
        },
      }))
      .reduce((acc, val) => ({ ...acc, ...val }), {});
  };

  const sendInvites = async () => {
    if (!isValid() || !organizationRole) {
      return;
    }

    const entitiesAccess: Record<string, EntitiesAccessConfig> = getSelectedEntitiesAccessPerCompany();
    const companiesToSubmit = getCompaniesWithSanitizedRoles(selectedCompanies, organizationRole);
    if (companiesToSubmit.some((company) => company.roles.length === 0)) {
      updateSelectedCompanies(companiesToSubmit);
      sendNotificationError(
        "Some companies have roles for products that are no longer available. Review the highlighted rows and pick a valid role before sending."
      );
      return;
    }
    updateSelectedCompanies(companiesToSubmit);

    const roles: CompanyWithRoles[] = companiesToSubmit.map((c) => ({ clientCode: c.clientCode, roles: c.roles }));

    setIsSending(true);

    const [, error] = await sendInvitations({
      emails,
      organizationRole,
      roles,
      entitiesAccess,
    });

    setIsSending(false);

    if (error) {
      handleError(error.message);
      logError(error, "[InviteMembersContext] sendInvitations");
      return;
    }

    await Promise.all([updatePendingInvites(), updateMembers()]);
    setInfoDialogData({
      title: "Invitation sent successfully",
      description: `${emails.length} members invited.`,
      icon: <EmailSuccessDialogIcon sx={{ width: 80, height: 80 }} />,
    });
  };

  const handleApplyMemberChanges = async () => {
    if (!isValid() || !managedMember || !organizationRole) {
      return;
    }

    const companiesToSubmit = getCompaniesWithSanitizedRoles(selectedCompanies, organizationRole);
    if (companiesToSubmit.some((company) => company.roles.length === 0)) {
      updateSelectedCompanies(companiesToSubmit);
      sendNotificationError(
        "Some companies have roles for products that are no longer available. Review the highlighted rows and pick a valid role before saving."
      );
      return;
    }
    updateSelectedCompanies(companiesToSubmit);

    const accessChanges: CompanyChange[] = companiesToSubmit.map((c) => {
      return {
        clientCode: c.clientCode,
        roles: c.roles,
      };
    });

    managedMember.clientsAccess.forEach((access) => {
      if (!accessChanges.some((a) => a.clientCode === access.clientCode)) {
        accessChanges.push({
          clientCode: access.clientCode,
          roles: [],
        });
      }
    });

    setIsSending(true);

    const [, error] = await updateOrganizationMemberAccess(managedMember.userId, {
      accessChanges,
      newOrganizationRole:
        organizationRole !== filterOrganizationRoles(managedMember.organizationPermissions.roles)[0]
          ? organizationRole
          : undefined,
      entitiesAccess: getSelectedEntitiesAccessPerCompany(),
    });

    setIsSending(false);

    if (error) {
      handleError(error.message);
      logError(error, "[InviteMembersContext] updateOrganizationMemberAccess");
      return;
    }

    updateMembers();

    setInfoDialogData({
      title: "Member updated successfully",
      description: `${managedMember.email} updated.`,
      icon: <UserAddDialogIcon sx={{ width: 80, height: 80 }} />,
    });

    if (isCurrentUser(managedMember.userId)) {
      setCurrentUserUpdated(true);
    }
  };

  const handleError = (error?: string) => {
    setIsSending(false);
    sendNotificationError(error || "Failed to send invitations.");
  };

  const clearEverything = () => {
    setEmails([]);
    setOrganizationRole(undefined);
    setSelectedCompanies([]);
    setSelectedMembersIds([]);
    setSelectedCompanyAccess(undefined);
    setManagedMember(undefined);
    setCurrentUserUpdated(false);
  };

  const updateOrganizationRole = (orgRole: OrganizationRole) => {
    setOrganizationRole(orgRole);
    setSelectedCompanyAccess(undefined);
    updateSelectedCompanies(selectedCompanies, orgRole);
  };

  const isAnyExternalUserSelected = (emailAddresses: string[]) =>
    emailAddresses.some((email) => isExternalUserEmail(email, organizationDomains ?? []));

  // Owner is hidden entirely when the user can't manage owners, but only disabled (with an explanation) when an external email is selected
  const allowedOrganizationRoles: readonly OrganizationRole[] = hasAccessToManageOrganizationOwners
    ? allOrganizationRoles
    : allOrganizationRoles.filter((role) => role !== "Org_Owner");

  const disabledOrganizationRoles: Partial<Record<OrganizationRole, string>> =
    hasAccessToManageOrganizationOwners && isAnyExternalUserSelected(emails)
      ? { Org_Owner: ownerRoleDisabledForExternalUsersReason }
      : {};

  const updateEmails = (newEmails: string[]) => {
    // eslint-disable-next-line @eslint-react/set-state-in-effect
    setEmails(newEmails);

    if (isAnyExternalUserSelected(newEmails) && organizationRole === "Org_Owner") {
      updateOrganizationRole("Org_Member");
    }
  };

  useEffect(() => {
    if (inviteMembersDialogData?.member) {
      const { organizationPermissions, clientsAccess } = inviteMembersDialogData.member;
      const role = filterOrganizationRoles(organizationPermissions.roles)[0];
      const fallbackOrganizationRole = allowedOrganizationRoles.length === 1 ? allowedOrganizationRoles[0] : undefined;
      const memberOrganizationRole = role && allowedOrganizationRoles.includes(role) ? role : fallbackOrganizationRole;
      if (memberOrganizationRole) {
        // eslint-disable-next-line @eslint-react/set-state-in-effect
        setOrganizationRole(memberOrganizationRole);
      }

      const memberCompanies: CompanyWithAccess[] = clientsAccess
        .filter((ca) => companies.some((c) => c.clientCode === ca.clientCode))
        .map((access) => ({
          clientCode: access.clientCode,
          roles: access.clientPermissions.roles,
          entityIds: access.clientPermissions.entitiesAccess ?? [],
          accessToAllEntities: access.clientPermissions.allowAccessToAllEntities,
        }));
      updateSelectedCompanies(memberCompanies, memberOrganizationRole);

      if (inviteMembersDialogData.action === "manage") {
        // eslint-disable-next-line @eslint-react/set-state-in-effect
        setManagedMember(inviteMembersDialogData.member);
        updateEmails([inviteMembersDialogData.member.email]);
      }
    }
    if (allowedOrganizationRoles.length === 1) {
      // eslint-disable-next-line @eslint-react/set-state-in-effect
      setOrganizationRole(allowedOrganizationRoles[0]);
    }
    // eslint-disable-next-line @eslint-react/exhaustive-deps
  }, [inviteMembersDialogData]);

  const getAvailableProductsWithRoles = useCallback(
    (clientCode: string): CompanyProductWithRoles[] => {
      if (!organizationRole) {
        return [];
      }

      const clientCompany = companies.find((c) => c.clientCode === clientCode);
      return getAvailableProductsWithRolesForCompany(organizationRole, clientCompany);
    },
    [companies, organizationRole]
  );

  return (
    <InviteMembersContext
      value={{
        organizationRole,
        emails,
        updateOrganizationRole,
        updateEmails,
        selectedCompanies,
        updateSelectedCompanies,
        getAvailableProductsWithRoles,
        onCompanyDelete: (clientCode: string) => {
          if (selectedCompanyAccess && selectedCompanyAccess[0].clientCode === clientCode) {
            setSelectedCompanyAccess(undefined);
          }
          updateSelectedCompanies(selectedCompanies.filter((company) => company.clientCode !== clientCode));
        },
        selectedCompanyAccess,
        updateSelectedCompanyAccess: (company: CompanyWithAccess, accessLevel: CompanyAccessLevel) => {
          setSelectedCompanyAccess([company, accessLevel]);
        },
        isValidConfiguration,
        sendInvites,
        distributePermissions: () => {
          if (!selectedCompanyAccess || !organizationRole) {
            return;
          }
          const [company, accessLevel] = selectedCompanyAccess;
          const updatedCompanies: CompanyWithAccess[] = selectedCompanies.map((c) => {
            if (accessLevel === "Product") {
              return {
                ...c,
                roles: [...company.roles],
              };
            }

            return c;
          });
          updateSelectedCompanies(updatedCompanies);
        },
        handleClose,
        getDialogTitle: () => {
          return managedMember ? `Manage user of ${clientTitle}` : `Invite users to ${clientTitle}`;
        },
        managedMember,
        applyMemberChanges: handleApplyMemberChanges,
        allowedOrganizationRoles,
        disabledOrganizationRoles,
        isCurrentUser,
        getCompanyEntities,
        entitiesFetchError,
      }}
    >
      {children}
      <InfoDialog isLoading={isLoading} data={infoDialogData} onClose={handleConfirmationClose} />
    </InviteMembersContext>
  );
};

export default InviteMembersContext;
